import { useState, useEffect, useCallback } from 'react';
import { toggleSidebarMenu, setMobileSidebarOpen } from '@app/store/reducers/ui';
import {
  removeWindowClass,
  scrollbarVisible,
} from '@app/utils/helpers';
import Header from '@app/modules/main/header/Header';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import MenuSidebar, { MENU, IMenuItem } from './menu-sidebar/MenuSidebar';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Loading } from '@app/components/Loading';

const Main = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const menuSidebarCollapsed = useAppSelector((state) => state.ui.menuSidebarCollapsed);
  const mobileSidebarOpen    = useAppSelector((state) => state.ui.mobileSidebarOpen);
  const currentUser          = useAppSelector((state) => state.auth.currentUser);

  const [isAppLoaded,        setIsAppLoaded]        = useState(false);
  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);

  // ── Auto-close mobile sidebar on route changes ─────────────────────────────
  useEffect(() => {
    dispatch(setMobileSidebarOpen(false));
  }, [location.pathname, dispatch]);

  // ── Auto-close mobile sidebar when resizing to desktop ─────────────────────
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1200) {
        dispatch(setMobileSidebarOpen(false));
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [dispatch]);

  // ── Close mobile sidebar (for overlay click) ───────────────────────────────
  const handleCloseMobileSidebar = useCallback(() => {
    dispatch(setMobileSidebarOpen(false));
  }, [dispatch]);

  // ── App ready once user is available ──────────────────────────────────────
  useEffect(() => {
    setIsAppLoaded(Boolean(currentUser));
  }, [currentUser]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  const getAllMenuItems = useCallback((items: IMenuItem[]): IMenuItem[] => {
    const flat: IMenuItem[] = [];
    items.forEach((item) => {
      if (item.path && item.shortcut) flat.push(item);
      if (item.children) flat.push(...getAllMenuItems(item.children));
    });
    return flat;
  }, []);

  const flatMenuItems = getAllMenuItems(MENU);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.key || typeof e.key !== 'string') return;
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      const key   = e.key.toUpperCase();
      const found = flatMenuItems.find(
        (item) =>
          typeof item.shortcut === 'string' &&
          item.shortcut.toUpperCase() === key,
      );
      if (found?.path) {
        e.preventDefault();
        navigate(found.path);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [flatMenuItems, navigate]);

  // ── Base body classes ──────────────────────────────────────────────────────
  useEffect(() => {
    removeWindowClass('register-page');
    removeWindowClass('login-page');
    removeWindowClass('hold-transition');

    document.body.classList.add('wrapper', 'bg-theme', 'bg-theme1');
    return () => {
      document.body.classList.remove('wrapper', 'bg-theme', 'bg-theme1');
    };
  }, []);

  // ── Sync wrapper classes with Redux state ─────────────────────────────────
  useEffect(() => {
    const isDesktop = window.innerWidth >= 1200;
    const isToggled = isDesktop ? menuSidebarCollapsed : mobileSidebarOpen;

    const wrappers = document.querySelectorAll('.wrapper');
    wrappers.forEach((w) => {
      if (menuSidebarCollapsed) {
        w.classList.add('desktop-collapsed');
      } else {
        w.classList.remove('desktop-collapsed');
      }

      if (mobileSidebarOpen) {
        w.classList.add('mobile-open');
      } else {
        w.classList.remove('mobile-open');
      }

      if (isToggled) {
        w.classList.add('toggled');
      } else {
        w.classList.remove('toggled');
        w.classList.remove('sidebar-hovered');
      }
    });

    if (isToggled) {
      document.body.classList.add('toggled');
    } else {
      document.body.classList.remove('toggled');
      document.body.classList.remove('sidebar-hovered');
    }

    if (mobileSidebarOpen) {
      document.body.classList.add('mobile-open');
    } else {
      document.body.classList.remove('mobile-open');
    }
  }, [menuSidebarCollapsed, mobileSidebarOpen]);

  // ── Scrollbar visibility ───────────────────────────────────────────────────
  const handleUIChanges = useCallback(() => {
    setIsScrollbarVisible(scrollbarVisible(document.body));
  }, []);

  useEffect(() => {
    handleUIChanges();
    window.addEventListener('scroll', handleUIChanges);
    window.addEventListener('resize', handleUIChanges);
    return () => {
      window.removeEventListener('scroll', handleUIChanges);
      window.removeEventListener('resize', handleUIChanges);
    };
  }, [handleUIChanges]);

  // ── jQuery plugins — runs AFTER React mounts the layout DOM ───────────────
  // This replaces app.js, select2-custom.js, and bs-stepper/main.js which
  // previously ran from index.html before React mounted, crashing with:
  //   "no element specified to initialize PerfectScrollbar"
  //   "$.(...).select2 is not a function"
  //   "Cannot read properties of null (reading 'classList')"
  useEffect(() => {
    if (!isAppLoaded) return; // wait until layout is fully rendered

    const w  = window as any;
    const $  = w.jQuery;
    if (!$) return;

    // ── PerfectScrollbar on sidebar ────────────────────────────────────────
    let ps: any = null;
    if (w.PerfectScrollbar) {
      const sidebarEl = document.querySelector('.sidebar-wrapper');
      if (sidebarEl) {
        ps = new w.PerfectScrollbar(sidebarEl, {
          wheelSpeed: 2,
          wheelPropagation: true,
          minScrollbarLength: 20,
        });
      }
    }

    // ── SimpleBar ──────────────────────────────────────────────────────────
    if (w.SimpleBar) {
      document.querySelectorAll('[data-simplebar]').forEach((el) => {
        new w.SimpleBar(el as HTMLElement);
      });
    }

    // ── MetisMenu ──────────────────────────────────────────────────────────
    if ($.fn?.metisMenu) {
      $('#accordionExample').metisMenu();
    }

    // ── Sidebar toggle (mobile) ────────────────────────────────────────────
    // React handles this via handleToggleMenuSidebar/Redux, but the
    // .toggle-icon overlay click is already wired in JSX below.
    // No jQuery needed for the toggle itself.

    // ── Back-to-top button visibility ──────────────────────────────────────
    const $backToTop = $('.back-to-top');
    const onScroll = () => {
      if ($(window).scrollTop() > 300) {
        $backToTop.fadeIn();
      } else {
        $backToTop.fadeOut();
      }
    };
    $(window).on('scroll.backToTop', onScroll);

    // ── Select2 — only on elements that exist now ─────────────────────────
    // Pages that need select2 should add class "select2-enable" to their
    // <select> elements. This is a global init for any that are present
    // in the layout (e.g. in Header dropdowns).
    if ($.fn?.select2) {
      $('.select2-enable').each(function (this: HTMLElement) {
        const $el = $(this);
        $el.select2({
          theme: 'bootstrap-5',
          width: $el.data('width') || '100%',
          placeholder: $el.data('placeholder') || '',
        });
      });
    }

    // ── Cleanup on unmount ─────────────────────────────────────────────────
    return () => {
      $(window).off('scroll.backToTop');
      ps?.destroy();

      // Destroy any select2 instances
      if ($.fn?.select2) {
        try { $('.select2-enable').select2('destroy'); } catch (_) {}
      }
    };
  }, [isAppLoaded]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const getAppTemplate = useCallback(() => {
    if (!isAppLoaded) return <Loading />;

    return (
      <>
        {/* Sidebar */}
        <MenuSidebar />

        {/* Top bar */}
        <Header />

        {/* Page content */}
        <div className="page-wrapper">
          <div className="page-content">
            <Outlet />
          </div>
        </div>

        {/* Mobile overlay — tapping closes sidebar */}
        <div className="overlay toggle-icon" onClick={handleCloseMobileSidebar} />

        {/* Back-to-top */}
        
          <a href="#top"
          className="back-to-top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <i className="bx bxs-up-arrow-alt" />
        </a>
      </>
    );
  }, [isAppLoaded, handleCloseMobileSidebar]);

  const isDesktop = typeof window !== 'undefined' ? window.innerWidth >= 1200 : true;
  const isToggled = isDesktop ? menuSidebarCollapsed : mobileSidebarOpen;

  return (
    <div
      className={`wrapper ${isToggled ? 'toggled' : ''} ${
        menuSidebarCollapsed ? 'desktop-collapsed' : ''
      } ${mobileSidebarOpen ? 'mobile-open' : ''}`}
    >
      {getAppTemplate()}
    </div>
  );
};

export default Main;